import { useEffect, useRef, useState } from 'react';

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Counts up to `target` once the element enters the viewport.
 * Renders the final value immediately when motion is reduced.
 */
export function useCountUp(
  target: number,
  duration = 1500,
): { ref: React.RefObject<HTMLSpanElement>; value: number } {
  const ref = useRef<HTMLSpanElement>(null);
  const [value, setValue] = useState(prefersReducedMotion() ? target : 0);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setValue(target);
      return;
    }
    const el = ref.current;
    if (!el) return;

    let raf = 0;
    let startTime = 0;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        const tick = (t: number): void => {
          if (!startTime) startTime = t;
          const p = Math.min((t - startTime) / duration, 1);
          // easeOutCubic
          setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
          if (p < 1) raf = window.requestAnimationFrame(tick);
        };
        raf = window.requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      window.cancelAnimationFrame(raf);
    };
  }, [target, duration]);

  return { ref, value };
}

/** Document scroll progress, 0 → 1. */
export function useScrollProgress(): number {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = (): void => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(window.scrollY / max, 1) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return progress;
}

/**
 * Cursor-tracking spotlight. Writes --mx/--my onto whichever
 * `[data-spotlight]` card the pointer is over, so CSS can paint a glow
 * that follows the cursor. Skipped entirely for reduced motion and
 * for coarse pointers (no hover to track).
 */
export function useSpotlight(rootRef: React.RefObject<HTMLElement>): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (prefersReducedMotion()) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const onMove = (e: MouseEvent): void => {
      const card = (e.target as HTMLElement).closest<HTMLElement>('[data-spotlight]');
      if (!card) return;
      const r = card.getBoundingClientRect();
      card.style.setProperty('--mx', `${e.clientX - r.left}px`);
      card.style.setProperty('--my', `${e.clientY - r.top}px`);
    };

    root.addEventListener('mousemove', onMove);
    return () => root.removeEventListener('mousemove', onMove);
  }, [rootRef]);
}

/**
 * Tracks which of several sections is currently in view — drives the
 * sticky process rail. Returns the index of the active element.
 */
export function useActiveSection(count: number): {
  refs: React.MutableRefObject<Array<HTMLElement | null>>;
  active: number;
} {
  const refs = useRef<Array<HTMLElement | null>>([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const els = refs.current.filter(Boolean) as HTMLElement[];
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const i = els.indexOf(visible.target as HTMLElement);
        if (i >= 0) setActive(i);
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: [0, 0.5, 1] },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [count]);

  return { refs, active };
}
